import { Link } from 'react-router-dom';
import React from 'react';
import MemorialStylesShowcase from '../components/MemorialStylesShowcase';
import EmailSignup from '../components/EmailSignup';

const Home: React.FC = () => {
  return (
    <div className="relative min-h-screen">
      {/* Hero Content */}
      <div className="pt-48 pb-20 md:pt-48 md:pb-32">
        {/* Hero Text */}
        <div className="text-center mb-16 md:mb-20">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
            <span className="text-white">Honor Their Memory</span>
            <span className="block mt-4 bg-gradient-to-r from-primary via-primary to-[#E6C45C] text-transparent bg-clip-text pb-2">
              Preserve Their Legacy
            </span>
          </h1>

          {/* CTA Buttons */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/create-memorial"
              className="px-8 py-4 text-lg font-semibold rounded-lg bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] hover:opacity-90 transition-opacity duration-200 shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            >
              Create a Memorial
            </Link>
            <Link
              to="/memorial-styles"
              className="px-8 py-4 text-lg font-semibold rounded-lg border-2 border-primary text-primary hover:bg-primary/10 transition-colors duration-200"
            >
              View Styles
            </Link>
          </div>
        </div>

        {/* Hero Image */}
        <div className="relative mx-auto max-w-6xl">
          {/* Gradient Glow Effect */}
          <div className="absolute -inset-x-32 -inset-y-16 bg-gradient-to-r from-primary/20 via-[#E6C45C]/20 to-primary/20 blur-3xl opacity-50 rounded-full" />
          
          <div className="relative rounded-xl overflow-hidden">
            <img
              src={`/images/Home-hero-laptop.svg?v=${Date.now()}`}
              alt="Digital Memorial Preview"
              className="w-full h-auto"
              onError={(e) => {
                console.error('Error loading hero image:', e);
                const imgElement = e.currentTarget;
                console.log('Attempted image path:', imgElement.src);
                imgElement.style.display = 'none';
              }}
              onLoad={(e) => {
                console.log('Hero image loaded successfully:', e.currentTarget.src);
              }}
            />
          </div>
        </div>
      </div>

      {/* Independent Web App Section */}
      <div className="py-20 md:py-32 relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="inline-block text-4xl md:text-5xl font-bold tracking-tight">
              <span className="block text-white">A Personal Space for</span>
              <span className="block mt-3 pb-6 bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text [-webkit-background-clip:text]">
                Your Cherished Memories
              </span>
            </h2>
            <p className="mt-4 text-lg text-[#FFFFFFCC] max-w-3xl mx-auto leading-relaxed">
              We create more than just a memorial page – we deploy independent apps that commemorate 
              your loved one's life. A private, permanent space that you and your family can 
              visit anytime, anywhere.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-16">
            {/* Independent Platform */}
            <div className="group relative p-8">
              {/* Background Effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-all duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/5 rounded-3xl transition-colors duration-700" />
              
              {/* Content Container */}
              <div className="relative flex flex-col items-center text-center">
                {/* Icon Container with Double Border */}
                <div className="relative w-24 h-24 transform group-hover:scale-110 transition-transform duration-700">
                  {/* Outer rotating border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-[#E6C45C] rounded-full animate-spin-slow opacity-70" />
                  {/* Inner rotating border */}
                  <div className="absolute inset-[3px] bg-gradient-to-r from-[#E6C45C] to-primary rounded-full animate-spin-slow opacity-50" style={{ animationDirection: 'reverse' }} />
                  {/* Icon background */}
                  <div className="absolute inset-[6px] bg-[#0A0A0A] rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-8 h-8 text-primary transform group-hover:scale-110 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                </div>

                {/* Text Content */}
                <div className="mt-8 relative">
                  <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors duration-300">
                    Your Own Private Space
                  </h3>
                  {/* Animated underline */}
                  <div className="h-px w-0 group-hover:w-full bg-gradient-to-r from-transparent via-primary to-transparent mx-auto transition-all duration-700 mt-3" />
                  <p className="mt-4 text-[#FFFFFFCC] leading-relaxed">
                    Unlike social media profiles or shared memorial platforms, we create completely private apps 
                    dedicated to your loved one. You'll have full control over who can visit and contribute, ensuring 
                    your memories remain protected and cherished.
                  </p>
                </div>

                {/* Hover Indicator */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </div>
            </div>

            {/* Custom Domain */}
            <div className="group relative p-8">
              {/* Background Effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-all duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/5 rounded-3xl transition-colors duration-700" />
              
              {/* Content Container */}
              <div className="relative flex flex-col items-center text-center">
                {/* Icon Container with Double Border */}
                <div className="relative w-24 h-24 transform group-hover:scale-110 transition-transform duration-700">
                  {/* Outer rotating border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-[#E6C45C] rounded-full animate-spin-slow opacity-70" />
                  {/* Inner rotating border */}
                  <div className="absolute inset-[3px] bg-gradient-to-r from-[#E6C45C] to-primary rounded-full animate-spin-slow opacity-50" style={{ animationDirection: 'reverse' }} />
                  {/* Icon background */}
                  <div className="absolute inset-[6px] bg-[#0A0A0A] rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-8 h-8 text-primary transform group-hover:scale-110 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                {/* Text Content */}
                <div className="mt-8 relative">
                  <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors duration-300">
                    Own Your Family Name
                  </h3>
                  {/* Animated underline */}
                  <div className="h-px w-0 group-hover:w-full bg-gradient-to-r from-transparent via-primary to-transparent mx-auto transition-all duration-700 mt-3" />
                  <p className="mt-4 text-[#FFFFFFCC] leading-relaxed">
                    Create and own your own unique family memorial address (like "smith.rip"). Each family 
                    name can only be registered once, making it truly yours forever. Build a growing 
                    collection of connected memorials for your family members, all in one special place.
                  </p>
                </div>

                {/* Hover Indicator */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </div>
            </div>

            {/* Full Customization */}
            <div className="group relative p-8">
              {/* Background Effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-all duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/5 rounded-3xl transition-colors duration-700" />
              
              {/* Content Container */}
              <div className="relative flex flex-col items-center text-center">
                {/* Icon Container with Double Border */}
                <div className="relative w-24 h-24 transform group-hover:scale-110 transition-transform duration-700">
                  {/* Outer rotating border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-[#E6C45C] rounded-full animate-spin-slow opacity-70" />
                  {/* Inner rotating border */}
                  <div className="absolute inset-[3px] bg-gradient-to-r from-[#E6C45C] to-primary rounded-full animate-spin-slow opacity-50" style={{ animationDirection: 'reverse' }} />
                  {/* Icon background */}
                  <div className="absolute inset-[6px] bg-[#0A0A0A] rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-8 h-8 text-primary transform group-hover:scale-110 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                </div>

                {/* Text Content */}
                <div className="mt-8 relative">
                  <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors duration-300">
                    Tell Their Story, Their Way
                  </h3>
                  {/* Animated underline */}
                  <div className="h-px w-0 group-hover:w-full bg-gradient-to-r from-transparent via-primary to-transparent mx-auto transition-all duration-700 mt-3" />
                  <p className="mt-4 text-[#FFFFFFCC] leading-relaxed">
                    Share precious photos, write heartfelt stories, and include meaningful music or videos. 
                    We'll help you create a beautiful tribute that truly reflects their personality and the 
                    impact they had on everyone's lives. We take care of everything.
                  </p>
                </div>

                {/* Hover Indicator */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What We Offer Section */}
      <div className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
              <span className="text-white">Everything You Need to</span>
              <div className="mt-2">
                <span className="text-primary">Commemorate</span>
                <span className="text-primary/90"> Your</span>
                <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text"> Loved One</span>
              </div>
            </h2>
          </div>

          {/* Service Cards */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Template Based Memorial */}
            <div className="group bg-gradient-to-b from-[#1A1A1A] to-[#141414] rounded-2xl p-8 border border-white/10 hover:border-primary/30 transition-all duration-500 relative overflow-hidden shadow-[0_0_25px_-5px_rgba(212,175,55,0.1)] hover:shadow-[0_0_35px_-5px_rgba(212,175,55,0.2)]">
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="flex flex-col h-full relative">
                <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-primary transition-colors duration-300">
                  Ready-Made Memorials
                </h3>
                <p className="text-[#FFFFFFCC] mb-8 flex-grow">
                  Begin your tribute today with our thoughtfully designed memorial styles. 
                  Each one has been created to honor your loved one with elegance and grace, 
                  making it easy to create something beautiful in minutes.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-white font-medium mb-1">Memory Wall & Condolence Book</span>
                      <span className="text-sm text-[#FFFFFFCC]">Full functionality out of the box</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-white font-medium mb-1">Ready in Days</span>
                      <span className="text-sm text-[#FFFFFFCC]">Simple process from start to finish</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-white font-medium mb-1">Customise Elements</span>
                      <span className="text-sm text-[#FFFFFFCC]">Add your own design preferences</span>
                    </div>
                  </div>
                </div>
                {/* Add Button */}
                <div className="mt-10 pt-6 border-t border-white/10 text-center">
                  <Link
                    to="/memorial-styles"
                    onClick={() => window.scrollTo(0, 0)}
                    className="inline-flex items-center justify-center px-8 py-3 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] hover:opacity-90 transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] group/button"
                  >
                    <span>View Memorial Styles</span>
                    <svg className="w-5 h-5 ml-2 transform group-hover/button:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Custom Memorial Service */}
            <div className="group bg-gradient-to-b from-[#1A1A1A] to-[#141414] rounded-2xl p-8 border border-white/10 hover:border-primary/30 transition-all duration-500 relative overflow-hidden shadow-[0_0_25px_-5px_rgba(212,175,55,0.1)] hover:shadow-[0_0_35px_-5px_rgba(212,175,55,0.2)]">
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="flex flex-col h-full relative">
                <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-primary transition-colors duration-300">
                  Personalized Memorial Creation
                </h3>
                <p className="text-[#FFFFFFCC] mb-8 flex-grow">
                  Let us create a completely unique memorial that perfectly captures your loved one's spirit. 
                  Our developers will work with you to bring your vision to life, handling every detail 
                  with care and respect.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-white font-medium mb-1">Personal Developer</span>
                      <span className="text-sm text-[#FFFFFFCC]">A person you can trust</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-white font-medium mb-1">Rich Media Support</span>
                      <span className="text-sm text-[#FFFFFFCC]">Videos, music, and special moments</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-white font-medium mb-1">Custom Features Developed</span>
                      <span className="text-sm text-[#FFFFFFCC]">We can develop any custom features you wish</span>
                    </div>
                  </div>
                </div>
                {/* Add Button */}
                <div className="mt-10 pt-6 border-t border-white/10 text-center">
                  <Link
                    to="/get-in-touch"
                    onClick={() => window.scrollTo(0, 0)}
                    className="inline-flex items-center justify-center px-8 py-3 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] hover:opacity-90 transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] group/button"
                  >
                    <span>Tell Us Your Idea</span>
                    <svg className="w-5 h-5 ml-2 transform group-hover/button:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Memorial Styles Showcase */}
      <MemorialStylesShowcase />

      {/* Email Signup Section */}
      <div className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <EmailSignup />
        </div>
      </div>
    </div>
  );
};

export default Home; 